import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, AlertCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/** Small in-line microphone control that transcribes German audio and
 *  calls onTranscript(text) so a parent text field can append the result. */
export default function MicRecorder({ onTranscript, disabled = false, testid = "mic" }) {
    const [state, setState] = useState("idle"); // idle | recording | transcribing
    const [seconds, setSeconds] = useState(0);
    const [error, setError] = useState("");
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => () => {
        stopTracks();
        if (timerRef.current) clearInterval(timerRef.current);
    }, []);

    const stopTracks = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const start = async () => {
        setError("");
        if (!navigator.mediaDevices?.getUserMedia) {
            setError("Browser unterstützt keine Aufnahme.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg", ""];
            let mime = "";
            for (const c of mimeCandidates) {
                if (!c || (window.MediaRecorder && MediaRecorder.isTypeSupported(c))) { mime = c; break; }
            }
            const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            recorderRef.current = rec;
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = handleStop;
            rec.start();
            setSeconds(0);
            timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
            setState("recording");
        } catch (e) {
            stopTracks();
            setError("Mikrofon-Zugriff verweigert. Bitte in den Browsereinstellungen erlauben.");
        }
    };

    const stop = () => {
        if (recorderRef.current && state === "recording") {
            recorderRef.current.stop();
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        }
    };

    const cancel = () => {
        if (recorderRef.current && state === "recording") {
            recorderRef.current.onstop = null;
            recorderRef.current.stop();
        }
        stopTracks();
        chunksRef.current = [];
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setState("idle");
        setSeconds(0);
    };

    const handleStop = async () => {
        stopTracks();
        const type = recorderRef.current?.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (blob.size < 800) {
            setState("idle"); setSeconds(0);
            toast.error("Aufnahme zu kurz. Bitte erneut versuchen.");
            return;
        }
        setState("transcribing");
        try {
            const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
            const fd = new FormData();
            fd.append("file", new File([blob], `note.${ext}`, { type }));
            const { data } = await api.post("/transcribe", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const text = (data?.text || "").trim();
            if (!text) {
                toast.info("Keine Sprache erkannt.");
            } else {
                onTranscript?.(text);
                toast.success("Transkription eingefügt");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transkription fehlgeschlagen");
        }
        setState("idle");
        setSeconds(0);
    };

    if (state === "idle") {
        return (
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    data-testid={`${testid}-start`}
                    onClick={start}
                    disabled={disabled}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-mes-border bg-white px-3 text-xs font-medium text-mes-ink hover:bg-mes-surfaceAlt disabled:opacity-50"
                    title="Sprachnotiz aufnehmen"
                >
                    <Mic className="h-4 w-4 text-mes-brand" strokeWidth={2} />
                    Sprachnotiz
                </button>
                {error && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-mes-danger">
                        <AlertCircle className="h-3 w-3" /> {error}
                    </span>
                )}
            </div>
        );
    }
    if (state === "recording") {
        return (
            <div className="flex items-center gap-2 rounded-md border border-mes-danger/40 bg-white px-2 py-1.5">
                <span className="inline-block h-2 w-2 animate-pulse-dot rounded-full bg-mes-danger" />
                <span className="tabular text-[13px] text-mes-ink tabular">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</span>
                <button
                    type="button" data-testid={`${testid}-stop`}
                    onClick={stop}
                    className="ml-1 inline-flex h-8 items-center gap-1 rounded-md bg-mes-brand px-2.5 text-xs font-semibold text-white hover:bg-mes-brandDark"
                >
                    <Square className="h-3.5 w-3.5" /> Stop
                </button>
                <button
                    type="button" data-testid={`${testid}-cancel`}
                    onClick={cancel}
                    className="inline-flex h-8 items-center rounded-md border border-mes-border px-2 text-xs text-mes-muted hover:text-mes-ink"
                >
                    Abbrechen
                </button>
            </div>
        );
    }
    return (
        <div className="inline-flex items-center gap-2 rounded-md border border-mes-border bg-white px-3 py-1.5 text-xs text-mes-muted" data-testid={`${testid}-transcribing`}>
            <Loader2 className="h-4 w-4 animate-spin text-mes-brand" />
            Transkribiere...
        </div>
    );
}
